import * as React from 'react';
import Moment from "moment";
import { IAccessRelationData, IRolePerrmission } from './AccessDomainModels';
import lang from "../Relations/RelationsLang";
import { IConfig } from './IModels';
import { ErrorMessagesList } from '../Relations/ErrorMessagesIconTooltip';
import { DatesFieldFormRow, LogDictionaryFormRow, LogEnvironmentFormRow, PasswordsFieldFormRow, RolePermissionSingleFormRow, TextareaFormRow, TextFieldFormRow } from '../Relations/FormsControls';
import { LoginFieldFormRow } from '../Relations/LoginFieldFormRow';

export interface IEditingAccessRelationData extends IAccessRelationData {
	copy: boolean,
}

export interface IEditRowControlResult {
	role: IRolePerrmission;
	from: Moment.Moment;
	to: Moment.Moment;

	login: string;
	password: string,
	environment: string,
	personAccountDescription: string,
	personAccountType: string,
	company: string,
	telephone: string,
	email: string,
	location: string,
	referenceNumber: string,
	systemName: string,

	description: string;
};

interface IProps {
	appName: string;
	row: IEditingAccessRelationData;
	onChange: (value: IEditRowControlResult) => void;
	config: IConfig;
}


function parseDatetime(datetime: Moment.Moment | string): Moment.Moment {
	if (!datetime)
		return null;

	var item: Moment.Moment
	if (typeof datetime === "string")
		item = Moment(datetime);
	else
		item = datetime;
	
	return Moment({
		year: item.year(),
		month: item.month(),
		day: item.date()
	});
}

export default function AccessRelationEditRow(props: IProps) {
	const [dateError, setDateError] = React.useState("");
	const [permissionRoleError, setPermissionRoleError] = React.useState("");
	const [descriptionError, setDescriptionError] = React.useState("");
	const [passwordError, setPasswordError] = React.useState("");
	const [loginError, setLoginError] = React.useState("");
	const [environmentError, setEnvironmentError] = React.useState("");
	const [systemNameError, setSystemNameError] = React.useState("");
	const [personAccountTypeError, setPersonAccountTypeError] = React.useState("");
	const [telephoneError, setTelephoneError] = React.useState("");
	const [emailError, setEmailError] = React.useState("");
	const [locationError, setLocationError] = React.useState("");
	const [companyError, setCompanyError] = React.useState("");
	const [personAccountDescriptionError, setPersonAccountDescriptionError] = React.useState("");
	const [referenceNumberError, setReferenceNumberError] = React.useState("");

	const [login, setLogin] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [rePassword, setRePassword] = React.useState("");
	const [environment, setEnvironment] = React.useState("");
	const [personAccountDescription, setPersonAccountDescription] = React.useState("");
	const [personAccountType, setPersonAccountType] = React.useState("");
	const [telephone, setTelephone] = React.useState("");
	const [email, setEmail] = React.useState("");
	const [location, setLocation] = React.useState("");
	const [company, setCompany] = React.useState("");
	const [referenceNumber, setReferenceNumber] = React.useState("");
	const [systemName, setSystemName] = React.useState("");

	const [description, setDescription] = React.useState("");
	const [selectedFrom, setSelectedFrom] = React.useState<Moment.Moment>(null);
	const [selectedTo, setSelectedTo] = React.useState<Moment.Moment>(null);

	const [selectedPermissionRole, setSelectedPermissionRole] = React.useState<IRolePerrmission>(null);

	React.useEffect(() => {
		setLogin(props.row.login || "");
		setPassword(props.row.password || "");
		setRePassword(props.row.password || "");
		setEnvironment(props.row.environment || "");
		setPersonAccountDescription(props.row.personAccountDescription || "");
		setPersonAccountType(props.row.personAccountType || "");
		setTelephone(props.row.telephone || "");
		setEmail(props.row.email || "");
		setLocation(props.row.location || "");
		setCompany(props.row.company || "");
		setReferenceNumber(props.row.referenceNumber || "");
		setSystemName(props.row.systemName || "");

		setDescription(props.row.description || "");

		setSelectedFrom(parseDatetime(props.row.from));
		setSelectedTo(parseDatetime(props.row.to));

		let role: IRolePerrmission = null;
		if (props.row.role)
			role = { ...props.row.role };
		setSelectedPermissionRole(role);
	}, []);
	

	React.useEffect(() => {
		if (isValid()) {
			const values = getValues();
			props.onChange(values)
		} else {
			props.onChange(null);
		}
	}, [
		login, password, rePassword, environment, personAccountDescription, personAccountType, telephone, email, location, company, referenceNumber, systemName,
		description, selectedFrom, selectedTo, selectedPermissionRole]);

	function isValid(): boolean {
		let fieldsConfig = props.config.fields;
		let from = selectedFrom;
		if (!from) {
			from = Moment({ h: 0, m: 0, s: 0, ms: 0 });
		}

		let to = selectedTo;

		let result = true;

		let permissionRoleErrorIsValid = "";
		let loginErrorIsValid = "";
		let passwordErrorIsValid = "";
		let environmentErrorIsValid = "";
		let systemNameErrorIsValid = "";
		let personAccountTypeErrorIsValid = "";
		let companyErrorIsValid = "";
		let telephoneErrorIsValid = "";
		let emailErrorIsValid = "";
		let locationErrorIsValid = "";
		let referenceNumberErrorIsValid = "";
		let personAccountDescriptionErrorIsValid = "";
		let dateErrorIsValid = "";
		let descriptionErrorIsValid = "";

		if (fieldsConfig.permissionRole.required && isApplication()) {
			if (selectedPermissionRole == null) {
				let label = lang.getDefaultByPrefix(fieldsConfig.permissionRole, "label", "rolePermissions");
				permissionRoleErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.login.required) {
			if (!login) {
				let label = lang.getDefaultByPrefix(fieldsConfig.login, "label", "login");
				loginErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.password.required) {
			if (!password) {
				let label = lang.getDefaultByPrefix(fieldsConfig.password, "label", "password");
				passwordErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (password) {
			if (password != rePassword) {
				passwordErrorIsValid = lang.retypePasswordError();
				result = false;
			}
		}

		if (fieldsConfig.environment.required) {
			if (!environment) {
				let label = lang.getDefaultByPrefix(fieldsConfig.environment, "label", "environment");
				environmentErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.systemName.required) {
			if (!systemName) {
				let label = lang.getDefaultByPrefix(fieldsConfig.systemName, "label", "systemName");
				systemNameErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.personAccountType.required) {
			if (!personAccountType) {
				let label = lang.getDefaultByPrefix(fieldsConfig.personAccountType, "label", "personAccountType");
				personAccountTypeErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.company.required) {
			if (!company) {
				let label = lang.getDefaultByPrefix(fieldsConfig.company, "label", "company");
				companyErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.telephone.required) {
			if (!telephone) {
				let label = lang.getDefaultByPrefix(fieldsConfig.telephone, "label", "telephone");
				telephoneErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.email.required) {
			if (!email) {
				let label = lang.getDefaultByPrefix(fieldsConfig.email, "label", "email");
				emailErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.location.required) {
			if (!location) {
				let label = lang.getDefaultByPrefix(fieldsConfig.location, "label", "location");
				locationErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.referenceNumber.required) {
			if (!referenceNumber) {
				let label = lang.getDefaultByPrefix(fieldsConfig.referenceNumber, "label", "referenceNumber");
				referenceNumberErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.personAccountDescription.required) {
			if (!personAccountDescription) {
				let label = lang.getDefaultByPrefix(fieldsConfig.personAccountDescription, "label", "personAccountDescription");
				personAccountDescriptionErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (to && from > to) {
			dateErrorIsValid = "control.relation.fromToErrorMessage";
			result = false;
		}

		if (fieldsConfig.dateTo.required) {
			if (!to) {
				let label = lang.getDefaultByPrefix(fieldsConfig.dateTo, "label", "to");
				dateErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (fieldsConfig.description.required) {
			if (!description) {
				let label = lang.getDefaultByPrefix(fieldsConfig.description, "label", "description");
				descriptionErrorIsValid = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		if (description.length > 255) {
			let label = lang.getDefaultByPrefix(fieldsConfig.description, "label", "description");
			descriptionErrorIsValid = lang.tooLongDescriptionError(label);
			result = false;
		}

		setPermissionRoleError(permissionRoleErrorIsValid);
		setPersonAccountDescriptionError(personAccountDescriptionErrorIsValid);
		setLoginError(loginErrorIsValid);
		setPasswordError(passwordErrorIsValid);
		setEnvironmentError(environmentErrorIsValid);
		setSystemNameError(systemNameErrorIsValid);
		setPersonAccountTypeError(personAccountTypeErrorIsValid);
		setCompanyError(companyErrorIsValid);
		setTelephoneError(telephoneErrorIsValid);
		setLocationError(locationErrorIsValid);
		setEmailError(emailErrorIsValid);
		setReferenceNumberError(referenceNumberErrorIsValid);
		setDescriptionError(descriptionErrorIsValid);
		setDateError(dateErrorIsValid);

		return result;
	}

	function isApplication(): boolean {
		return props.row.resource.kind == "Application";
	}

	function getValues(): IEditRowControlResult {
		let from = selectedFrom;
		if (!from) {
			from = Moment({ h: 0, m: 0, s: 0, ms: 0 });
		}

		let to = selectedTo;
		
		return {
			from: from,
			to: to,
			login: login,
			password: password,
			environment: environment,
			personAccountDescription: personAccountDescription,
			personAccountType: personAccountType,
			company: company,
			telephone: telephone,
			email: email,
			location: location,
			referenceNumber: referenceNumber,
			systemName: systemName,
			role: selectedPermissionRole,
			description: description,
		}
	}

	const fields = props.config.fields;
	return <div className="edit-input-row">
		<ErrorMessagesList errorMessages={props.row.errorMessages} />

		<RolePermissionSingleFormRow
			config={fields.permissionRole}
			errorText={permissionRoleError}
			isApplication={isApplication()}

			applicationId={props.row.resource.id}
			value={selectedPermissionRole}
			onSelect={(role: IRolePerrmission) => setSelectedPermissionRole(role)}

			role={props.row.role}
		/>

		<LoginFieldFormRow
			name="login"
			errorText={loginError}
			config={fields.login}
			value={login}
			onChange={(value) => setLogin(value)}

			applicationId={props.row.resource.id}
			employeeId={props.row.employee?.id}

			onChangedLocation={(value) => {
				setLocation(value);
				setLocationError("");
			}}

			onChangedReferenceNumber={(value) => {
				setReferenceNumber(value);
				setReferenceNumberError("");
			}}
		/>

		<PasswordsFieldFormRow
			name="password"
			errorText={passwordError}
			config={fields.password}
			value1={password}
			value2={rePassword}
			onChange={(password1, password2) => {

				setPassword(password1);
				setRePassword(password2);
			
			}}
		/>

		<LogEnvironmentFormRow
			name="environment"
			errorText={environmentError}
			config={fields.environment}
			value={environment}
			onChange={(value) => {
				setEnvironment(value);
			}}
			application={props.row.resource}
		/>

		<TextFieldFormRow
			name="systemName"
			errorText={systemNameError}
			config={fields.systemName}
			value={systemName}
			onChange={(value) => {
				setSystemName(value);
			}}
		/>

		<LogDictionaryFormRow
			name="personAccountType"
			dictionaryType="PersonAccountType"
			errorText={personAccountTypeError}
			config={fields.personAccountType}
			value={personAccountType}
			onChange={(value) => {
				setPersonAccountType(value);
			}}
		/>

		<LogDictionaryFormRow
			name="company"
			dictionaryType="Company"
			errorText={companyError}
			config={fields.company}
			value={company}
			onChange={(value) => {
				setCompany(value);
			}}
		/>

		<TextFieldFormRow
			name="telephone"
			errorText={telephoneError}
			config={fields.telephone}
			value={telephone}
			onChange={(value) => {
				setTelephone(value);
			}}
		/>

		<TextFieldFormRow
			name="email"
			errorText={emailError}
			config={fields.email}
			value={email}
			onChange={(value) => {
				setEmail(value);
			}}
		/>

		<TextFieldFormRow
			name="location"
			errorText={locationError}
			config={fields.location}
			value={location}
			onChange={(value) => {
				setLocation(value);
				setLocationError("");
			}}
		/>

		<TextFieldFormRow
			name="referenceNumber"
			errorText={referenceNumberError}
			config={fields.referenceNumber}
			value={referenceNumber}
			onChange={(value) => {
				setReferenceNumber(value);
				setReferenceNumberError("");
			}}
		/>

		<TextareaFormRow
			name="personAccountDescription"
			config={fields.personAccountDescription}
			errorText={personAccountDescriptionError}
			value={personAccountDescription}
			onChange={value => {
				setPersonAccountDescription(value);
			}}
		/>

		<DatesFieldFormRow
			editRow={!!props.row.relationId}
			errorText={dateError}
			dateFromConfig={fields.dateFrom}
			dateToConfig={fields.dateTo}
			from={selectedFrom}
			to={selectedTo}
			onChange={(from, to) => {
				setSelectedFrom(from);
				setSelectedTo(to);
			}}
		/>

		<TextareaFormRow
			name="description"
			errorText={descriptionError}
			config={fields.description}
			value={description}
			onChange={(value) => {
				setDescription(value);
			}}
		/>
	</div>
}